import { forEach, take, min, max } from 'lodash';
import { Point, Bounds, BoundsBuilder, Matrix22 } from '../geometry';
import { Figures } from './figures';
import { whenAll, if$ } from 'gear';
import { Group } from './group';

export const Packing = {};

const pack_q = function(size,page,margin) {
  return Math.floor( (page+margin)/(size+margin) );
};

const directPack = function(w,h,lx,ly,qx,qy,margin) {
  const positions = [];
  for(let y = 0; y < qy ; ++y) {
    for(let x = 0; x < qx ; ++x) {
      positions.push(new Point(lx+x*(w+margin),ly+y*(h+margin)));
    }
  }
  return positions;
};

const directExtraPack = function(w,h,lx,ly,qx,qy,margin) {
  const positions = [];
  for(let x = 0; x < qx ; ++x) {
    for(let y = 0; y < qy ; ++y) {
      positions.push(new Point(lx+x*(w+margin),ly+y*(h+margin)));
    }
  }
  return positions;
};

const oneDirectionSimpleGangData = function(objX, objY, pageX, pageY, margin) {
  const mainQx = pack_q(objX,pageX,margin);
  const mainQy = pack_q(objY,pageY,margin);
  const mainQ  = mainQx*mainQy;

  const remX = pageX - mainQx*(objX+margin);
  const rightQx = pack_q(objY,remX ,margin);
  const rightQy = pack_q(objX,pageY,margin);
  const rightQ  = rightQx*rightQy;

  const remY = pageY - mainQy*(objY+margin);
  const topQx = pack_q(objY,pageX,margin);
  const topQy = pack_q(objX,remY ,margin);
  const topQ  = topQx*topQy;

  return rightQ > 0 ? { count: mainQ + rightQ,
                        main : { qx: mainQx, qy: mainQy },
                        extra: { place: 'right', qx: rightQx, qy: rightQy } }
                    : { count: mainQ + topQ,
                        main : { qx: mainQx, qy: mainQy },
                        extra: topQ > 0 ? { place: 'top', qx: topQx, qy: topQy } : null };
};

const simpleGangData = function(objX, objY, pageX, pageY, margin) {
  const rs = oneDirectionSimpleGangData(objX, objY, pageX, pageY, margin);
  const rr = oneDirectionSimpleGangData(objY, objX, pageX, pageY, margin);
  return rs.count >= rr.count ? ( rs.rotated = false, rs ) : ( rr.rotated = true, rr );
};

const simpleGangPositions = function(w, h, pageW, pageH, margin, maxNumber) {
  const r = simpleGangData(w,h,pageW,pageH,margin);
  const rotatedPack = r.rotated ;
  const e = r.extra;

  if(rotatedPack) { const a = w; w = h; h = a; } // w <--> h

  let mp_hx = r.main.qx*(w+margin);
  if( e && e.place==='top' ) {
    const e_hx = e.qx*(h+margin);
    if( e_hx > mp_hx ) {
      mp_hx = e_hx;
    }
  }
  let mp_hy = r.main.qy*(h+margin);
  if( e && e.place==='right' ) {
    const e_hy = e.qy*(w+margin);
    if( e_hy > mp_hy ) {
      mp_hy = e_hy;
    }
  }

  // Offset to center result
  const offset_x = ( pageW - ( mp_hx + ( e && e.place==='right' ? e.qx*(h+margin) : 0 ) - margin ) ) / 2;
  const offset_y = ( pageH - ( mp_hy + ( e && e.place==='top'   ? e.qy*(w+margin) : 0 ) - margin ) ) / 2;

  let main  = directPack(w,h,offset_x,offset_y,r.main.qx,r.main.qy,margin);
  let extra = !r.extra ? [] : directExtraPack(h,w,
        ( e.place==='right' ? mp_hx : 0 )+ offset_x,
        ( e.place==='top'   ? mp_hy : 0 )+ offset_y,
        e.qx, e.qy, margin );

  if( maxNumber !== undefined ) {
    if( main.length > maxNumber ) {
      main = take(main,maxNumber);

      // Extra vertical offset
      const min_y = min(main,'y').y,
            max_y = max(main,'y').y+h ;
      const desired_min_y = ( pageH - (max_y-min_y) ) / 2;
      const o_y = desired_min_y - min_y ;
      let o_x = 0;

      if( extra.length > 0 ) {
        extra = [];
        // Extra horizontal offset
        const min_x = min(main,'x').x,
              max_x = max(main,'x').x+w,
              desired_min_x = ( pageW - (max_x-min_x) ) / 2;

        o_x = desired_min_x - min_x ;
      }

      forEach( main, s => { s.x += o_x; s.y += o_y; } );
      forEach( extra, s => { s.x += o_x; s.y += o_y; } );
    }
    else {
      extra = take(extra,maxNumber-main.length);
    }
  }

  return rotatedPack ? { straight: extra, rotated: main  }
                     : { straight: main , rotated: extra };
};


Packing.simpleGangCount$ = function( figures, pageW, pageH, margin) {
  return Figures.bounds$(figures).then( bounds => {
  return Packing.simpleGangCount(bounds.width,bounds.height,pageW,pageH,margin);
  });
};

Packing.simpleGangPositions$ = function( figures, pageW, pageH, margin, maxNumber) {
  return Figures.bounds$(figures).then( bounds => {
  const w = bounds.width, h = bounds.height;
  return simpleGangPositions(w,h,pageW,pageH,margin, maxNumber);
  });
};

Packing.simpleGang$ = function(figure, pageW, pageH, margin, maxNumber) {
  return figure.bounds$().then( bounds => {
    const w = bounds.width, h = bounds.height;
    const positions = simpleGangPositions(w,h,pageW,pageH,margin, maxNumber);
    if( maxNumber === undefined ) {
      maxNumber = positions.length;
    }

    const offset = Point.minus( bounds.lx_ly );
    const cloneFigure = function(pos,rotated) {
      const pf = figure.clone();
      pf.translate(offset);
      if(rotated) {
        pf.rotateAround(new Point(0,0),-90);
        pf.translate(0,bounds.width);
      }
      pf.translate(pos);
      return pf;
    };

    const packedFigures = [];
    forEach( positions.straight, pos => {
      packedFigures.push( cloneFigure(pos,false) );
    });
    forEach( positions.rotated, pos => {
      packedFigures.push( cloneFigure(pos,true) );
    });

    return packedFigures;
  });
};

Packing.simpleGangCount = function(objX, objY, pageX, pageY, margin) {
  return simpleGangData(objX,objY,pageX,pageY,margin).count;
};

// guillotine packing
const wrapWithGroup = function(item) {
  let wrapped = null;
  
  if (item.typeName === "Document") {
    wrapped = Group.create(item.figures);
    wrapped.wasGrouped = true;
  }
  else if (item.constructor === Array && item[0].isFigure === true){
    wrapped = Group.create(item);
    wrapped.wasGrouped = true;
  }
  else if (item.isFigure === true)
    wrapped = item;

  return wrapped;
}

const createBin = function(lx, ly, hx, hy){
  return {
    frame: new Bounds(lx, ly, hx, hy),
    freeSpaces: [new Bounds(lx, ly, hx, hy)],
    items: []
  };
};

const tryToFitInBin = function(item, bounds, bin, margin, fitFunction, allowRotation){
  const itemBounds = bounds;

  let bestFit = Infinity;
  let bestFitIndex = -1;
  let bestLx = 0.0;
  let bestLy = 0.0;
  let bestWidth = 0.0; 
  let bestHeight = 0.0;
  let rotation = false;
  let freeSpaceIndex = 0;
  
  while (freeSpaceIndex < bin.freeSpaces.length)
  {
    const freeSpace = bin.freeSpaces[freeSpaceIndex];
    const isFrameLx = freeSpace.lx == bin.frame.lx;
    const isFrameLy = freeSpace.ly == bin.frame.ly;
    const isFrameHx = freeSpace.hx == bin.frame.hx;
    const isFrameHy = freeSpace.hy == bin.frame.hy;
    const lx = freeSpace.lx + (isFrameLx ? 0 : margin / 2);
    const ly = freeSpace.ly + (isFrameLy ? 0 : margin / 2);
    const extraWidth  = (isFrameLx ? 0 : margin / 2) + (isFrameHx && ((lx + itemBounds.width  + margin / 2) >= freeSpace.hx) ? 0 : margin / 2);
    const extraHeight = (isFrameLy ? 0 : margin / 2) + (isFrameHy && ((ly + itemBounds.height + margin / 2) >= freeSpace.hy) ? 0 : margin / 2);
    const freeWidth = freeSpace.width;
    const freeHeight = freeSpace.height;
    const sameWidth = Math.abs(freeWidth  - (itemBounds.width  + extraWidth )) < 1e-8;
    const sameHeight = Math.abs(freeHeight - (itemBounds.height + extraHeight)) < 1e-8;
    const sameWidthRotated  = Math.abs(freeWidth  - (itemBounds.height + extraWidth )) < 1e-8;
    const sameHeightRotated = Math.abs(freeHeight - (itemBounds.width  + extraHeight)) < 1e-8;
    const fits = (sameWidth || freeWidth > itemBounds.width +  extraWidth) && (sameHeight || freeHeight > itemBounds.height + extraHeight);
    const fitsRotated = (sameWidthRotated || freeWidth > itemBounds.height + extraWidth) && (sameHeightRotated || freeHeight > itemBounds.width + extraHeight);
    let preferRotation = false;
    let currentFit = Infinity;

    switch(fitFunction)
    {
      case "bssf":
        if (fits)
          currentFit = Math.min(freeWidth - (itemBounds.width + extraWidth), freeHeight - (itemBounds.height + extraHeight));

        if (allowRotation && fitsRotated)
        {
          const rotatedFit = Math.min(freeWidth - (itemBounds.height + extraWidth) , freeHeight - (itemBounds.width + extraHeight));
          preferRotation = rotatedFit < currentFit;
          if (preferRotation)
            currentFit = rotatedFit;
        }
        break;
      case "baf":
      default: // same as baf
        if (fits)
          currentFit = freeWidth * freeHeight - (itemBounds.width + extraWidth) * (itemBounds.height + extraHeight);

        if (allowRotation && fitsRotated)
        {
          const rotatedFit = freeWidth * freeHeight - (itemBounds.height + extraWidth) * (itemBounds.width + extraHeight);
          preferRotation = rotatedFit < currentFit;
          if (preferRotation)
            currentFit = rotatedFit;
        }
    }
    
    if( currentFit < bestFit )
    {
      bestFit = currentFit;
      bestFitIndex = freeSpaceIndex;
      rotation = preferRotation;
      bestLx = lx;
      bestLy = ly;
      bestWidth = (preferRotation ? itemBounds.height : itemBounds.width) + extraWidth;
      bestHeight = (preferRotation ? itemBounds.width  : itemBounds.height) + extraHeight;
    }

    freeSpaceIndex++;
  }

  if(bestFitIndex == -1)
    return false;

  // move item to position inside free space
  moveToFinalPosition(item, itemBounds, bestLx, bestLy, rotation);
  
  // add item to bin
  bin.items.push(item);

  // split the unused space, if any
  split(bin, bestFitIndex, bestWidth, bestHeight);

  return true;
};

const moveToFinalPosition = function(item, itemBounds, lx, ly, rotation) {
  const transformation = rotation 
                         ? new Matrix22({s00: 0, s01: -1, s10: 1, s11: 0, dx: lx + itemBounds.height + itemBounds.ly, dy: ly - itemBounds.lx})
                         : new Matrix22({s00: 1, s01: 0, s10: 0, s11: 1, dx: lx - itemBounds.lx, dy: ly - itemBounds.ly});
  item.transform(transformation);
}

const addFreeSpace = function(bin, freeSpace){
  let grew = false;
  do
  {
    grew = false;
    let freeSpaceIndex = 0;
    let currentFreeSpace = bin.freeSpaces[freeSpaceIndex];
    
    while( freeSpaceIndex < bin.freeSpaces.length )
    {
      if(Bounds.areOverlapping(freeSpace,  currentFreeSpace)) {
        if(freeSpace.lx == currentFreeSpace.lx && freeSpace.hx == currentFreeSpace.hx) {
          let builder = new BoundsBuilder();
          builder.update(freeSpace);
          builder.updateWithPoint(new Point(freeSpace.lx, Math.min(freeSpace.ly, currentFreeSpace.ly)));
          builder.updateWithPoint(new Point(freeSpace.lx, Math.max(freeSpace.hy, currentFreeSpace.hy)));
          freeSpace = builder.bounds;
          currentFreeSpace = bin.freeSpaces.splice(freeSpaceIndex, 1);
          grew = true;
        }
        else if( (freeSpace.ly == currentFreeSpace.hy || freeSpace.hy == currentFreeSpace.ly) && // vertically adjacent
                 (freeSpace.lx <  currentFreeSpace.lx || freeSpace.hx >  currentFreeSpace.hx) &&
                 (!(freeSpace.lx >  currentFreeSpace.lx || freeSpace.hx <  currentFreeSpace.hx)) && // ensure we don't grow in x, only shrink
                 ((freeSpace.width - currentFreeSpace.width) * freeSpace.height * 5 < currentFreeSpace.height * currentFreeSpace.width))
        {
          // heuristic: remove excess area area of freeSpace to merge with currentFreeSpace
          // if that means we can gain at least 5 times the area removed (which means a single bigger free space)
          freeSpace.lx = currentFreeSpace.lx;
          freeSpace.hx = currentFreeSpace.hx;
          let builder = new BoundsBuilder();
          builder.update(freeSpace);
          builder.updateWithPoint(new Point(freeSpace.lx, Math.min(freeSpace.ly, currentFreeSpace.ly)));
          builder.updateWithPoint(new Point(freeSpace.lx, Math.max(freeSpace.hy, currentFreeSpace.hy)));
          freeSpace = builder.bounds;
          currentFreeSpace = bin.freeSpaces.splice(freeSpaceIndex, 1);
          grew = true;
        }
        else if( freeSpace.ly == currentFreeSpace.ly &&
          freeSpace.hy == currentFreeSpace.hy )
        {
          let builder = new BoundsBuilder();
          builder.update(freeSpace);
          builder.updateWithPoint(new Point(Math.min(freeSpace.lx, currentFreeSpace.lx), freeSpace.ly));
          builder.updateWithPoint(new Point(Math.max(freeSpace.hx, currentFreeSpace.hx), freeSpace.ly));
          freeSpace = builder.bounds;
          currentFreeSpace = bin.freeSpaces.splice(freeSpaceIndex, 1);
          grew = true;
        }
        else if( (freeSpace.lx == currentFreeSpace.hx || freeSpace.hx == currentFreeSpace.lx) && // horizontally adjacent
                 (freeSpace.ly <  currentFreeSpace.ly || freeSpace.hy >  currentFreeSpace.hy) &&
                 (!(freeSpace.ly >  currentFreeSpace.ly || freeSpace.hy <  currentFreeSpace.hy)) && // ensure we don't grow in y, only shrink
                 ((freeSpace.height - currentFreeSpace.height) * freeSpace.width * 5 < currentFreeSpace.height * currentFreeSpace.width))
        {
          // heuristic: remove excess area area of freeSpace to merge with lIter
          // if that means we can gain at least 5 times the area removed (which means a single bigger free space)
          freeSpace.ly = currentFreeSpace.ly;
          freeSpace.hy = currentFreeSpace.hy;
          let builder = new BoundsBuilder();
          builder.update(freeSpace);
          builder.updateWithPoint(new Point(Math.min(freeSpace.lx, currentFreeSpace.lx), freeSpace.ly));
          builder.updateWithPoint(new Point(Math.max(freeSpace.hx, currentFreeSpace.hx), freeSpace.ly));
          freeSpace = builder.bounds;
          currentFreeSpace = bin.freeSpaces.splice(freeSpaceIndex, 1);
          grew = true;
        }
        else
          freeSpaceIndex++;
      }
      else
        freeSpaceIndex++;
    }
  } while( grew );
  
  bin.freeSpaces.push(freeSpace);
}

const splitHorizontally = function(bin, freeSpace, width, height) {
  const {lx, ly, hx, hy} = freeSpace;  
  if(height < freeSpace.height) {
    let newSpace = new Bounds(lx, ly + height, hx, hy);
    addFreeSpace(bin, newSpace);
  }

  if(width < freeSpace.width) {
    let newSpace = new Bounds(lx + width, ly, hx, ly + height);
    addFreeSpace(bin, newSpace);
  }
}

const splitVertically = function(bin, freeSpace, width, height) {
  const {lx, ly, hx, hy} = freeSpace;
  if(height < freeSpace.height) {
    let newSpace = new Bounds(lx, ly + height, lx + width, hy);
    addFreeSpace(bin, newSpace);
  }

  if(width < freeSpace.width) {
    let newSpace = new Bounds(lx + width, ly, hx, hy);
    addFreeSpace(bin, newSpace);
  }
}

const split = function(bin, freeSpaceIndex, width, height) {
  let space = bin.freeSpaces[freeSpaceIndex];
  bin.freeSpaces.splice(freeSpaceIndex, 1);

  if( space.width < space.height )
    splitHorizontally(bin, space, width, height);
  else
    splitVertically(bin, space, width, height);
}

/*
 config = {
  width: 100,
  height: 100,
  margins: {
    "top": 0,
    "bottom": 0,
    "left": 0,
    "right": 0,
    "inner": 5
  },
  options: { 
    "allowRotation": true,
    "fitFunction": "BAF" // or BSSF,
    "groupInputIfNeeded": false,
    "drawBins": true
  }
};
*/
Packing.guillotine$ = function(items, config) {
  let bins = [];
  const {margins, options, width, height} = config;
  const fitFunction = (options.fitFunction || "baf").toLowerCase();
  const allowRotation = options.allowRotation !== false;
  const groupInputIfNeeded = options.groupInputIfNeeded === true;
  const drawBins = options.drawBins !== false;
  const leftMargin = margins.left > 0 ? margins.left : 0;
  const rightMargin = margins.right > 0 ? margins.right : 0;
  const topMargin = margins.top > 0 ? margins.top : 0;
  const bottomMargin = margins.bottom > 0 ? margins.bottom : 0;
  const workingItems = []
  
  bins.push(createBin(leftMargin,
                      bottomMargin,
                      width - rightMargin,
                      height - topMargin));
  
  const binWidth = bins[0].frame.width;
  const binHeight = bins[0].frame.height;

  let boundPromises = [];
  for(let i = 0, c = items.length; i < c; ++i) {
    let item = groupInputIfNeeded ? wrapWithGroup(items[i]) : items[i];
    workingItems.push(item);
    boundPromises.push(item.bounds$());
  }

  return whenAll(boundPromises).then(function(results) {
    // check that that all the items are smaller than a bin's dimension
    let sorted = [];

    for(let i = 0; i < results.length; ++i) {
      const bounds = results[i];
      if((bounds.height > binHeight || bounds.width > binWidth) &&
        (allowRotation && (bounds.width > binHeight || bounds.height > binWidth))) {
        throw new Error("an item is larger than the bin");
      }
      sorted.push({item: workingItems[i], bounds: bounds});
    }

    // sort desc by area
    sorted = sorted.sort((x, y) => y.bounds.area - x.bounds.area);

    for(let i = 0; i < sorted.length; ++i) {
      const {item, bounds} = sorted[i];
      
      let j = 0, l = bins.length;
      while(j < l && !tryToFitInBin(item, bounds, bins[j], margins.inner, fitFunction, allowRotation))
        j++; 
        
      if(j >= l) {
        let lastBin = bins[bins.length-1];
        let newBin  = createBin(lastBin.frame.lx,
                                lastBin.frame.ly + height,
                                lastBin.frame.hx,
                                lastBin.frame.hy + height);
        bins.push(newBin);
        tryToFitInBin(item, bounds, newBin, margins.inner, fitFunction, allowRotation);
      }
    }

    if (groupInputIfNeeded)
      for(let i = 0; i < bins.length; ++i) 
        bins[i].items = bins[i].items.map(x => x. wasGrouped ? x.ungroup() : x);

    return if$(drawBins, function() {
      let binBounds = []
  
      for(let i = 0; i < bins.length; ++i)
        binBounds.push(Cx.bounds$(bins[i].items));
      
      return Cx.whenAll(binBounds, function(results) {
        for(let i = 0; i< results.length; ++i) {
          let currentBin = bins[i];
          let lx = results[i].lx - (width - results[i].width)   / 2 ; 
          let ly = results[i].ly - (height - results[i].height) / 2 ;
          let binFrame = new Bounds(lx, ly, lx + width, ly + height);
          currentBin.items.push(Cx.Shape.CreateRectangle({bounds: binFrame, pen: new Cx.Pen({color: Cx.Color.Black})}));
        }
  
        return bins;
      })
    }).else$(() => Cx.resolve(bins));
  });
}

//binPack
const getFiguresToPack$ = function(item) {
  const {document, width, height, quantity, aspectRatio} = item;

  if(quantity === 0){
    throw new Error("must contain at least 1 repetition of this item")
  }

  let docFigures = [];
  for( let i = 0; i < document.figures.length; i++) {
    docFigures.push(document.figures[i]);
  }
  
  let ar = aspectRatio || {};
  const locked = ar.locked === undefined ? false : ar.locked, 
        constrainWidth = ar.constrainWidth  === undefined ? false : ar.constrainWidth,
        constrainHeight = ar.constrainHeight  === undefined ? false : ar.constrainHeight;
  
  return Figures.bounds$(docFigures).then(function(bounds) {
    let scaleX = width / bounds.width;
    let scaleY = height / bounds.height;
    if(locked) {
      if (constrainWidth != constrainHeight)
        scaleX = scaleY = constrainWidth ? scaleX : scaleY;
      else if(constrainWidth)
      {
        if (scaleX * bounds.height > height)
          scaleX = scaleY;
        if (scaleY * bounds.width > width)
          scaleY = scaleX;
      }
    }
    return Figures.scale$(docFigures, scaleX, scaleY);
  }).then(function(scaledFigures) {
      let toPack = [];
      for(let i = 0, c = quantity; i < c; ++i) {
        toPack.push(Group.create(scaledFigures));
      }
      return toPack;
  });
}

/*
let items = [{
    document: ...,
    width: 100,
    height: 100,
    quantity: 5,
    aspectRatio: {
      locked: true,
      constrainWidth: true,
      constrainHeight: true
    }
  },
  ...
];
*/
Packing.binPack$ = function(items, config) {
  let figuresToPackP = [];
  for(let i = 0; i < items.length; ++i) {
    figuresToPackP.push(getFiguresToPack$(items[i]));
  }

  return whenAll(figuresToPackP).then(function(figuresToPackR) {
    let figuresToPack = figuresToPackR.flat();
    return Packing.guillotine$(figuresToPack, config);
  });
}

const placeInRow = function(rowItems, ly, gap) {
  let lx = 0, rowHeight = 0;
  for(let i = 0, c = rowItems.length; i < c; ++i) {
    let {item, bounds} = rowItems[i];
    item.translate(lx - bounds.lx, ly - bounds.ly);
    lx += bounds.width + gap;
    rowHeight = Math.max(bounds.height, rowHeight);
  }
  return rowHeight;
}

/*
config =
{
  width: 100,
  rotateAllInput: false,
  sortInput: false,
  itemsGap: 5
};
*/
Packing.stack$ = function(items, config) {
  const stackingWidth = config.width || 1000;
  const gap = config.itemsGap || 5;
  
  let promises = [];
  let workingItems = [];

  for(let i = 0, c = items.length; i < c; ++i) {
    let wrappedItem = { item: wrapWithGroup(items[i]) };
    workingItems.push(wrappedItem);
    promises.push(
    if$(config.rotateAllInput === true, function(){
      return wrappedItem.item.rotate$(90).then(x => x.bounds$());
    }).else$(function() {
      return wrappedItem.item.bounds$();
    }));
  }

  return whenAll(promises).then(function(results) {
    const itemCount = workingItems.length;
    for(let i = 0; i < itemCount; ++i) 
      workingItems[i].bounds = results[i];
    
    if (config.sortInput === true)
      workingItems = workingItems.sort((x, y) => y.bounds.width - x.bounds.width);
    
    let rowWidth = 0, s = 0, ly = 0;

    for (let i = 0; i < itemCount; ++i) {
      let currentItemWidth = workingItems[i].bounds.width;
      rowWidth += currentItemWidth;

      if(rowWidth > stackingWidth) {
        let e = i;
        rowWidth = currentItemWidth;
        if ( e === s )
          e++, rowWidth = 0;

        ly += placeInRow(workingItems.slice(s, e), ly, gap) + gap;
        s = e;
      }
      else
        rowWidth += gap; // add gap before next item
    }
    
    // place the last row
    placeInRow(workingItems.slice(s, itemCount), ly, gap);
        
    return workingItems.map(x => x.item.wasGrouped ? x.item.ungroup() : x.item);
  });
}




/** WEBPACK FOOTER **
 ** ./src/cdl/figure/packing.js
 **/